import { Box, Tabs, Tab, IconButton, SpeedDialAction, SpeedDial, Chip, Tooltip } from "@mui/material";
import { useEffect } from "react";
import { useContext } from "react";
import { useCallback, useMemo, useState } from "react";

import Draggable from "react-draggable";
import { useLocation } from "react-router-dom";
import { useHistory } from "react-router-dom";
import {
	ActiveEnrollmentStatus,
	EnrollmentSchema,
	EnrollmentStatus,
	NextCurrentPlanOpenEnrollment,
	OpenEnrollmentByPlan,
	Status,
	VEBEnrollmentStatus
} from "../../../types/enrollment.types";
import { OverallPremiums } from "../../../types/overallPremiums.types";
import { GroupEnrollmentContext, PaycheckContext, ThemeContext, AuthContext } from "../../../contexts";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
	HealthPlanBrochure,
	HelpingOurOwnPlanBrochure,
	KSOPPlanBrochure,
	GSTDPlanBrochure,
	ClinicCardPlanBrochure,
	GLTDClass1PlanBrochure,
	GLTDClass2PlanBrochure,
	GLIPlanBrochure
} from "../../../assets";

import "./enrollment.css";

import SpeedDialIcon from "@mui/material/SpeedDialIcon";
import EnrollmentTabLabel from "./enrollmentTabLabel/EnrollmentTabLabel";
import HealthPlanBranding from "./plans/healthPlan/branding/Branding";
import HealthPlanForm from "./plans/healthPlan/form/HealthPlanForm";
import ClinicCardForm from "./plans/clinicCard/form/ClinicCardForm";
import ClinicCardBranding from "./plans/clinicCard/branding/Branding";
import KsopForm from "./plans/ksop/form/KsopForm";
import KsopBranding from "./plans/ksop/branding/KsopBranding";
import PDForm from "./plans/pd/PDForm";
import GltdForm from "./plans/GLTDClass1/form/GltdForm";
import GltdBranding from "./plans/GLTDClass1/branding/GltdBranding";
import GstdForm from "./plans/gstd/form/GstdForm";
import GstdBranding from "./plans/gstd/branding/GstdBranding";
import CceForm from "./plans/cce/form/CceForm";
import DownloadIcon from "@mui/icons-material/Download";
import AttachEmailIcon from "@mui/icons-material/AttachEmail";
import EmailIcon from "@mui/icons-material/Email";

import { api } from "../../../utils/api";
import { Member } from "../../../types/employee.types";
import { Plan } from "../../../types/plan.types";
import { OpenEnrollment } from "../../../types/openEnrollment.types";
import { Plans, PlanTab } from "../../../types/components/enrollmentProps.types";
import { numberToDollarStr } from "../../../utils/commonFunctions/dollar";
import EnrollmentAcknowledgement from "./enrollmentAcknowledgement/EnrollmentAcknowledgement";
import { Brochure } from "../../../assets";
import downloadFile, { downloadBrochureFile } from "../../../utils/commonFunctions/downloadFile";
import PlanAcknowledgement from "./plans/planAknowledgement/PlanAknowledgement";
import { CCeBranding, GliBranding, HOOBranding } from "..";
import GLTDClass2Branding from "./plans/GLTDClass2/branding/GlassClass2Branding";
import GLIForm from "./plans/gli/GLIForm/GLIForm";
import LincolnForm from "./plans/GLTDClass2/form/LincolnForm";
import { trackPromise } from "react-promise-tracker";
import initCapitalize from "../../../utils/commonFunctions/initCapitalize";
import { dollarize } from "../../../utils/commonFunctions/dollarize";
import {
	buttonCaptionShortNames,
	coreEnrollmentArray,
	coreEnrollmentNext
} from "../../../constants/coreEnrollmentNext";
import { PLAN_NAME } from "../../../constants/plan";
import GLIFormv2 from "./plans/gli/GLIFormV2/GLIFormv2";
import PlanAcknowledgement2 from "./plans/planAknowledgement/AlwaysOpenPlanAcknowledgement";
import AlwaysOpenPlanAcknowledgement from "./plans/planAknowledgement/AlwaysOpenPlanAcknowledgement";
import { ALL_PLANS } from "../../../constants/allPlan";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
	[`&.${tableCellClasses.head}`]: {
		backgroundColor: theme.palette.action.hover
	},
	[`&.${tableCellClasses.body}`]: {
		fontSize: 14
	}
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
	"&:nth-of-type(even)": {
		backgroundColor: theme.palette.action.hover
	},
	// hide last border
	"&:last-child td, &:last-child th": {
		border: 0
	}
}));

function a11yProps(index: number) {
	return {
		id: `vertical-tab-${index}`,
		"aria-controls": `vertical-tabpanel-${index}`
	};
}

type ButtonCaption = {
	nextPlan: string;
	prevPlan: string;
};

type CoreButtonCaption = {
	"Health Plan": ButtonCaption;
	"Clinic Card": ButtonCaption;
	"Group Short Term Disability": ButtonCaption;
	"Group Long Term Disability(Class 1)": ButtonCaption;
	"Group Long Term Disability(Class 2)": ButtonCaption;
	KSOP: ButtonCaption;
	"Helping Our Own": ButtonCaption;
	"Christmas Club Election": ButtonCaption;
	"Group Life Insurance": ButtonCaption;
};

const Enrollment = (): JSX.Element => {
	const [activeTab, setActiveTab] = useState<string>();
	const [visitedPlans, setVisitedPlans] = useState<string[]>([]);
	const [activeEnrollmentStatus, setActiveEnrollmentStatus] = useState<ActiveEnrollmentStatus>();
	const [nextOfCurrentPlan, setNextOfCurrentPlan] = useState<NextCurrentPlanOpenEnrollment>();
	const [enrollments, setEnrollments] = useState<EnrollmentSchema[]>([]);
	const [VEBEnrollmentStatus, setVEBEnrollmentStatus] = useState<VEBEnrollmentStatus | null>(null);
	const [openEnrollmentByPlan, setOpenEnrollmentByPlan] = useState<OpenEnrollmentByPlan | null>(null);
	const [hasSetPlanTabs, setHasSetPlanTabs] = useState<boolean>(false);
	const { currentEnrollment, deleteCurrentEnrollment } = useContext(GroupEnrollmentContext);
	const [planTabs, setPlanTabs] = useState<PlanTab[]>([
		{
			plan_name: "Group Short Term Disability"
		},
		{
			plan_name: "Group Long Term Disability(Class 1)"
		},
		{
			plan_name: "Group Long Term Disability(Class 2)"
		},
		{
			plan_name: "KSOP"
		},
		{
			plan_name: "Helping Our Own"
		},
		{
			plan_name: "Christmas Club Election"
		},
		{
			plan_name: "Group Life Insurance"
		}
	]);
	const [openEnrollments, setOpenEnrollments] = useState<
		{
			open_enrollment: OpenEnrollment & {
				plan: Plan;
			};
		}[]
	>([]);
	const [alwaysOpenEnrollments, setAlwaysOpenEnrollments] = useState<Plan[]>([]);
	const [buttonCaption, setButtonCaption] = useState<CoreButtonCaption>({
		"Health Plan": {
			nextPlan: "",
			prevPlan: ""
		},
		"Clinic Card": {
			nextPlan: "",
			prevPlan: ""
		},
		"Group Short Term Disability": {
			nextPlan: "",
			prevPlan: ""
		},
		"Group Long Term Disability(Class 1)": {
			nextPlan: "",
			prevPlan: ""
		},
		"Group Long Term Disability(Class 2)": {
			nextPlan: "",
			prevPlan: ""
		},
		KSOP: {
			nextPlan: "",
			prevPlan: ""
		},
		"Helping Our Own": {
			nextPlan: "",
			prevPlan: ""
		},
		"Christmas Club Election": {
			nextPlan: "",
			prevPlan: ""
		},
		"Group Life Insurance": {
			nextPlan: "",
			prevPlan: ""
		}
	});
	const [enrollmentStatus, setEnrollmentStatus] = useState<EnrollmentStatus | null>(null);
	const [value, setValue] = useState<string>();
	const [overallPremiums, setOverallPremiums] = useState<OverallPremiums[]>([]);
	const [dependents, setDependents] = useState<Member[]>([]);
	const [totalPremium, setTotalPremium] = useState(0.0);
	const location = useLocation();
	const { theme } = useContext(ThemeContext);
	const { member } = useContext(AuthContext);
	const { paycheck } = useContext(PaycheckContext);
	const urlSearchParams: URLSearchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
	const height = screen.height;
	const [showOverallPremium, setShowOverallPremium] = useState(true);
	const history = useHistory();
	const [currentStage, setCurrentStage] = useState(0);
	const overallPremiumChipColor = {
		SUBMIITED: {
			background_color: "#d5e5d9",
			text_color: "#4cb464"
		},
		CURRENT: {
			background_color: "#f0ebc5",
			text_color: "#c9b30c"
		},
		WAIVED: {
			background_color: "#f7e9e9",
			text_color: "#eb2a2a"
		}
	};

	const speedDialActions = [
		// eslint-disable-next-line prettier/prettier
		{
			name: <div className="tooltip-speed-dial">Download&nbsp;Brochure</div>,
			icon: <DownloadIcon />,
			actionName: "download_brochure"
		},
		{
			name: <div className="tooltip-speed-dial">Email&nbsp;Brochure</div>,
			icon: <AttachEmailIcon />,
			actionName: "email_brochure"
		}
	];
	const [speedDialOpen, setSpeedDialOpen] = useState(true);

	const handleOpen = () => setSpeedDialOpen(true);

	const handleClose = () => setSpeedDialOpen(false);

	const initialVisitedPlans = useCallback(() => {
		if (
			paycheck?.employee_class === "FT - Full Time" ||
			paycheck?.employee_class === "PTIN - Part Time Insurance Eligible"
		) {
			setVisitedPlans(["Health Plan"]);
		}

		if (paycheck?.employee_class === "PT - Part Time") {
			setVisitedPlans(["Clinic Card"]);
		}
	}, [paycheck?.employee_class]);

	const handleChange = useCallback(
		(event: React.SyntheticEvent<Element, Event>, newValue: string) => {
			setActiveTab(newValue);
			setValue(newValue);
			deleteCurrentEnrollment();

			const _enrollmentVisitedArray: string[] = [];

			coreEnrollmentArray.forEach((elememt, index) => {
				paycheck?.employee_class === "FT - Full Time"
					? elememt !== "Clinic Card" || index !== 1
						? _enrollmentVisitedArray.push(elememt)
						: null
					: paycheck?.employee_class === "PT - Part Time"
					? elememt !== "Health Plan" || index !== 0
						? _enrollmentVisitedArray.push(elememt)
						: null
					: _enrollmentVisitedArray.push(elememt);
			});

			_enrollmentVisitedArray.forEach((element, index) => {
				if (element === newValue) {
					const selectedRange = _enrollmentVisitedArray.slice(0, index + 1);
					setVisitedPlans([...selectedRange]);
				}
			});
		},
		[deleteCurrentEnrollment, paycheck]
	);

	const getPlanShortNames = (planName: string): string => {
		switch (planName) {
			case "Health Plan":
				return "HP";
				break;
			case "Clinic Card":
				return "CC";
				break;
			case "Group Short Term Disability":
				return "GSTD";
				break;
			case "Group Long Term Disability(Class 1)":
				return "GLTD (Class 1)";
				break;
			case "Group Long Term Disability(Class 2)":
				return "GLTD (Class 2)";
				break;
			case "KSOP":
				return "KSOP";
				break;
			case "Helping Our Own":
				return "HOO";
				break;
			case "Christmas Club Election":
				return "CCE";
				break;
			case "Group Life Insurance":
				return "GLI";
				break;
			default:
				return "N/A";
				break;
		}
	};

	const handleButtonCaptionSetter = useCallback(() => {
		const arrayForIteration: string[] = [];
		const currentPlanName: any = urlSearchParams.get("step");

		const _storedIndexOfNextPlan: any[] = [];
		const _storedNonOpenIndexBefore: any[] = [];
		const _storedIndexOfPrevPlan: number[] = [];

		// sorting the plans which have open enrollment status
		if (enrollmentStatus != null || enrollmentStatus != undefined) {
			for (let idx = 0; idx < coreEnrollmentArray.length; idx++) {
				if (paycheck?.employee_class === "FT - Full Time") {
					if (coreEnrollmentArray[idx] === "Clinic Card") coreEnrollmentArray.splice(idx, 1);
				}

				if (paycheck?.employee_class === "PT - Part Time") {
					if (coreEnrollmentArray[idx] === "Health Plan") coreEnrollmentArray.splice(idx, 1);
				}

				arrayForIteration.push(coreEnrollmentArray[idx]);

				const planName = coreEnrollmentArray[idx] as keyof EnrollmentStatus;

				// handling the special case
				if (
					enrollmentStatus[planName].openEnrollment === "OPENED" ||
					coreEnrollmentArray[idx] === "Helping Our Own"
				) {
					const elementsIndex = coreEnrollmentArray.indexOf(planName);

					_storedIndexOfNextPlan.push({
						idx: elementsIndex,
						plan_name: planName
					});
				}

				if (
					enrollmentStatus[planName].openEnrollment === "CLOSED" ||
					coreEnrollmentArray[idx] === "Helping Our Own"
				) {
					const elememtNonOpenIndex = coreEnrollmentArray.indexOf(planName);

					_storedNonOpenIndexBefore.push({
						idx: elememtNonOpenIndex,
						plan_name: planName
					});
				}
			}
		}

		// for (let index = 0; index < arrayForIteration.length; index++) {
		// 	for (let next_pt = index; next_pt < _storedIndexOfNextPlan.length; next_pt++) {
		// 		if (index === _storedIndexOfNextPlan[index]) {
		// 			next_pt++;
		// 			continue;
		// 		}

		// 		if (_storedIndexOfNextPlan[index] > index) index++;

		// 		const planNameUpdate: string = arrayForIteration[index];
		// 		const _updated_next_plan: string = getPlanShortNames(_storedIndexOfNextPlan[next_pt].plan_name);

		// 		setButtonCaption((prevButtonCaption: any) => {
		// 			return Object.assign({}, prevButtonCaption, {
		// 				[planNameUpdate]: {
		// 					...prevButtonCaption.planName,
		// 					nextPlan: _updated_next_plan
		// 				}
		// 			});
		// 		});

		// 		// console.log("_current_index", index);
		// 	}
		// }
	}, [enrollmentStatus, paycheck, urlSearchParams]);

	const handleChangePlanButtonClick = useCallback(
		(direction: string) => {
			const planName: any = urlSearchParams.get("step");

			const arrayForIteration: string[] = coreEnrollmentArray;

			if (direction === "FORWARD") {
				const indexofCurrentPlan = arrayForIteration.indexOf(planName);

				if (enrollmentStatus != null || enrollmentStatus != undefined) {
					for (let index = indexofCurrentPlan + 1; index < arrayForIteration.length; index++) {
						if (paycheck?.employee_class === "FT - Full Time") {
							if (arrayForIteration[index] === "Clinic Card") arrayForIteration.splice(index, 1);
						}

						if (paycheck?.employee_class === "PT - Part Time") {
							if (arrayForIteration[index] === "Health Plan") arrayForIteration.splice(index, 1);
						}

						const nextOpened =
							enrollmentStatus[arrayForIteration[index] as keyof EnrollmentStatus].openEnrollment ===
							"OPENED";

						// special condition for helping our own
						if (arrayForIteration[index] === "Helping Our Own") {
							const nextOpenedPlan: string = arrayForIteration[index];
							history.push("?step=" + "Helping Our Own" + "&stage=0");
							setActiveTab("Helping Our Own");
							setValue("Helping Our Own");
							const helpingOurOwnIndex = arrayForIteration.indexOf("Helping Our Own");
							const selectedRange = arrayForIteration.slice(
								indexofCurrentPlan + 1,
								helpingOurOwnIndex + 1
							);

							// eslint-disable-next-line arrow-parens
							setButtonCaption((prevButtonCaption) => {
								return Object.assign({}, prevButtonCaption, {
									planName: {
										...prevButtonCaption["Helping Our Own"], // Keep the existing properties of planName
										nextPlan: nextOpenedPlan
									}
								});
							});

							setVisitedPlans([...visitedPlans, ...selectedRange]);
							break;
						}

						if (arrayForIteration[index] === "Christmas Club Election") {
							const nextOpenedPlan: string = arrayForIteration[index];
							history.push("?step=" + "Christmas Club Election" + "&stage=0");
							setActiveTab("Christmas Club Election");
							setValue("Christmas Club Election");
							const christmasClubIndex = arrayForIteration.indexOf("Christmas Club Election");
							const selectedRange = arrayForIteration.slice(
								indexofCurrentPlan + 1,
								christmasClubIndex + 1
							);

							// eslint-disable-next-line arrow-parens
							setButtonCaption((prevButtonCaption) => {
								return Object.assign({}, prevButtonCaption, {
									planName: {
										...prevButtonCaption["Christmas Club Election"],
										nextPlan: nextOpenedPlan
									}
								});
							});

							setVisitedPlans([...visitedPlans, ...selectedRange]);
							break;
						}

						if (nextOpened === true) {
							const nextOpenedPlan: string = arrayForIteration[index];
							const indexOfNextOpenPlan = arrayForIteration.indexOf(nextOpenedPlan);
							history.push("?step=" + nextOpenedPlan + "&stage=0");
							setActiveTab(nextOpenedPlan);
							setValue(nextOpenedPlan);
							const selectedRange = arrayForIteration.slice(
								indexofCurrentPlan + 1,
								indexOfNextOpenPlan + 1
							);

							// eslint-disable-next-line arrow-parens
							setButtonCaption((prevButtonCaption) => {
								return Object.assign({}, prevButtonCaption, {
									[planName]: {
										...prevButtonCaption[planName as keyof CoreButtonCaption], // Keep the existing properties of planName
										nextPlan: nextOpenedPlan
									}
								});
							});

							setVisitedPlans([...visitedPlans, ...selectedRange]);
							break;
						}
					}
				}
			}

			if (direction === "BACKWARD") {
				let visitedArray: string[];
				const indexofCurrentPlan = arrayForIteration.indexOf(planName);
				if (enrollmentStatus != null || enrollmentStatus != undefined) {
					for (let index = indexofCurrentPlan - 1; index >= 0; index--) {
						// ommiting the elements from the array, according to the employee_class
						if (paycheck?.employee_class === "FT - Full Time") {
							if (arrayForIteration[index] === "Clinic Card") arrayForIteration.splice(index, 1);
						}

						if (paycheck?.employee_class === "PT - Part Time") {
							if (arrayForIteration[index] === "Health Plan") arrayForIteration.splice(index, 1);
						}

						// special condition for helping our own
						if (arrayForIteration[index] === "Helping Our Own") {
							history.push("?step=" + "Helping Our Own" + "&stage=0");
							setActiveTab("Helping Our Own");
							setValue("Helping Our Own");
							visitedArray = [...visitedPlans];
							const helpingOurOwnIndex = arrayForIteration.indexOf("Helping Our Own");
							const updatedArrayElements = visitedArray.slice(0, helpingOurOwnIndex + 1);
							setVisitedPlans(Object.assign([], updatedArrayElements));
							break;
						}

						// special condition for Christmas Club Election
						if (arrayForIteration[index] === "Christmas Club Election") {
							history.push("?step=" + "Christmas Club Election" + "&stage=0");
							setActiveTab("Christmas Club Election");
							setValue("Christmas Club Election");
							visitedArray = [...visitedPlans];
							const christmasClubIndex = arrayForIteration.indexOf("Christmas Club Election");
							const updatedArrayElements = visitedArray.slice(0, christmasClubIndex + 1);
							setVisitedPlans(Object.assign([], updatedArrayElements));
							break;
						}

						// targetting the plans which are open for enrollment
						const previousOpen =
							enrollmentStatus[arrayForIteration[index] as keyof EnrollmentStatus].openEnrollment ===
							"OPENED";

						if (arrayForIteration)
							if (previousOpen === true) {
								const previousOpenedPlan: string = arrayForIteration[index];
								history.push("?step=" + previousOpenedPlan + "&stage=0");
								setActiveTab(previousOpenedPlan);
								setValue(previousOpenedPlan);

								// now setting the stepper on previous button click
								visitedArray = [...visitedPlans];
								const currentPlanIndex = arrayForIteration.indexOf(previousOpenedPlan);
								const updatedArrayElements = visitedArray.slice(0, currentPlanIndex + 1);
								setVisitedPlans(Object.assign([], updatedArrayElements));
								break;
							}
					}
				}
			}
		},

		[enrollmentStatus, history, paycheck?.employee_class, urlSearchParams, visitedPlans]
	);

	const getEnrollmentStatus = useCallback(
		(
			planName: string,
			enrollments: any,
			openEnrollments: {
				open_enrollment: OpenEnrollment & {
					plan: Plan;
				};
			}[]
		): {
			status: {
				enrollment: Status;
				openEnrollment: Status;
			};
			openEnrollmentByPlanItem: {
				[key: string]: OpenEnrollment & {
					plan: Plan;
				};
			} | null;
		} => {
			let enrollment: Status = "CLOSED";

			for (let enrollmentIdx = 0; enrollmentIdx < enrollments.length; enrollmentIdx++) {
				if (enrollments[enrollmentIdx]?.plan_name === planName) {
					enrollment = enrollments[enrollmentIdx].enrollment_status;
					break;
				}
			}
			let openEnrollment: Status = "CLOSED";
			let openEnrollmentByPlanItem: {
				[key: string]: OpenEnrollment & {
					plan: Plan;
				};
			} | null = null;
			for (let openEnrollmentIdx = 0; openEnrollmentIdx < openEnrollments.length; openEnrollmentIdx++) {
				if (openEnrollments[openEnrollmentIdx].open_enrollment.plan.plan_name === planName) {
					openEnrollment = "OPENED";

					openEnrollmentByPlanItem = {
						[planName]: openEnrollments[openEnrollmentIdx].open_enrollment
					};
					break;
				}
			}
			return {
				status: {
					enrollment,
					openEnrollment
				},
				openEnrollmentByPlanItem
			};
		},
		[]
	);

	const getActiveEnrollmentStatus = useCallback((activeEnrollments: EnrollmentSchema[]) => {
		const _activeEnrollmentStatus = {
			"Health Plan": false,
			"Clinic Card": false,
			KSOP: false,
			"Group Short Term Disability": false,
			"Group Long Term Disability(Class 1)": false,
			"Group Long Term Disability(Class 2)": false,
			"Group Life Insurance": false,
			"Helping Our Own": false,
			"Christmas Club Election": false
		};
		Object.entries(_activeEnrollmentStatus).forEach(([planName, value], index) => {
			activeEnrollments.forEach((activeEnrollment: EnrollmentSchema) => {
				const { enrollment_status, plan_name } = activeEnrollment;
				if (plan_name === planName) {
					_activeEnrollmentStatus[planName as keyof ActiveEnrollmentStatus] = true;
				}
			});
		});
		setActiveEnrollmentStatus(Object.assign({}, _activeEnrollmentStatus));
	}, []);

	const getEnrollmentInitData = useCallback(
		async (resetPlanTabs = true) => {
			const { openEnrollments, dependents, enrollments, activeEnrollments, alwaysOpenEnrollments } =
				await trackPromise(api.pages.getEnrollmentInitData());
			getActiveEnrollmentStatus(activeEnrollments);
			setOpenEnrollments(Object.assign([], openEnrollments));
			setAlwaysOpenEnrollments(Object.assign([], alwaysOpenEnrollments));
			if (enrollments.length > 0) {
				setEnrollments(Object.assign([], enrollments));
			}
			let _enrollmentStatus: EnrollmentStatus = {
				"Health Plan": {
					enrollment: "CLOSED",
					openEnrollment: "CLOSED"
				},
				"Clinic Card": {
					enrollment: "CLOSED",
					openEnrollment: "CLOSED"
				},
				KSOP: {
					enrollment: "CLOSED",
					openEnrollment: "CLOSED"
				},
				"Group Short Term Disability": {
					enrollment: "CLOSED",
					openEnrollment: "CLOSED"
				},
				"Group Long Term Disability(Class 1)": {
					enrollment: "CLOSED",
					openEnrollment: "CLOSED"
				},
				"Group Life Insurance": {
					enrollment: "CLOSED",
					openEnrollment: "CLOSED"
				},
				"Helping Our Own": {
					enrollment: "OPENED",
					openEnrollment: "OPENED"
				},
				"Christmas Club Election": {
					enrollment: "OPENED",
					openEnrollment: "OPENED"
				},
				"Group Long Term Disability(Class 2)": {
					enrollment: "OPENED",
					openEnrollment: "OPENED"
				}
			};
			// const _enrollments = [
			// 	{ plan: { plan_name: "Clinic Card" }, status: "ACTIVE" },
			// 	// { plan: { plan_name: "Health Plan" }, status: "WAIVED" },
			// 	{ plan: { plan_name: "Group Short Term Disability" }, status: "WAIVED" },
			// 	{ plan: { plan_name: "KSOP" }, status: "WAIVED" }
			// ];
			let _openEnrollmentByPlan: OpenEnrollmentByPlan = {
				"Health Plan": null,
				"Clinic Card": null,
				KSOP: null,
				"Group Short Term Disability": null,
				"Group Long Term Disability(Class 1)": null,
				"Group Long Term Disability(Class 2)": null,
				"Group Life Insurance": null,
				"Helping Our Own": null,
				"Christmas Club Election": null
			};
			Object.entries(_enrollmentStatus).forEach(([planName], index) => {
				const { status, openEnrollmentByPlanItem } = getEnrollmentStatus(
					planName,
					enrollments,
					openEnrollments
				);
				_enrollmentStatus = { ..._enrollmentStatus, [planName]: status };
				if (openEnrollmentByPlanItem !== null) {
					_openEnrollmentByPlan = {
						..._openEnrollmentByPlan,
						...openEnrollmentByPlanItem
					};
				}
			});
			setOpenEnrollmentByPlan(Object.assign({}, _openEnrollmentByPlan));
			setEnrollmentStatus(Object.assign({}, _enrollmentStatus));
			setDependents(Object.assign([], dependents));
			if (resetPlanTabs === false) {
				return;
			}
			if (paycheck?.employee_class === "FT - Full Time") {
				const _planTab: PlanTab = { plan_name: "Health Plan" };

				setPlanTabs((prevPlanTabs: PlanTab[]) => Object.assign([], [_planTab, ...prevPlanTabs]));
			} else if (paycheck?.employee_class === "PT - Part Time") {
				const _planTab: PlanTab = { plan_name: "Clinic Card" };

				setPlanTabs((prevPlanTabs: PlanTab[]) => Object.assign([], [_planTab, ...prevPlanTabs]));
			} else if (paycheck?.employee_class === "PTIN - Part Time Insurance Eligible") {
				const _planTabs: PlanTab[] = [{ plan_name: "Health Plan" }, { plan_name: "Clinic Card" }];
				setPlanTabs((prevPlanTabs: PlanTab[]) => Object.assign([], [..._planTabs, ...prevPlanTabs]));
			}
			setHasSetPlanTabs(true);
		},
		[getActiveEnrollmentStatus, getEnrollmentStatus, paycheck?.employee_class]
	);

	const initNextOfCurrentPlans = useCallback(() => {
		if (paycheck?.employee_class === "FT - Full Time") {
			const nextCurrentPlanOpenEnrollment: NextCurrentPlanOpenEnrollment = coreEnrollmentNext;
			delete nextCurrentPlanOpenEnrollment["Clinic Card"];
			nextCurrentPlanOpenEnrollment["Health Plan"] = "Group Short Term Disability";
			setNextOfCurrentPlan(nextCurrentPlanOpenEnrollment);
		} else if (paycheck?.employee_class === "PT - Part Time") {
			const nextCurrentPlanOpenEnrollment: NextCurrentPlanOpenEnrollment = coreEnrollmentNext;
			delete nextCurrentPlanOpenEnrollment["Health Plan"];
			setNextOfCurrentPlan(nextCurrentPlanOpenEnrollment);
		} else if (paycheck?.employee_class === "PTIN - Part Time Insurance Eligible") {
			const nextCurrentPlanOpenEnrollment: NextCurrentPlanOpenEnrollment = coreEnrollmentNext;
			nextCurrentPlanOpenEnrollment["Health Plan"] = "Clinic Card";
			setNextOfCurrentPlan(nextCurrentPlanOpenEnrollment);
		}
	}, [paycheck?.employee_class]);

	const handleOverallPremiumClick = () => {
		setShowOverallPremium((prevShowOverallPremium: boolean) => !prevShowOverallPremium);
	};

	const getOverallPremium = useCallback(() => {
		const _overallPremiums: any[] = [];
		let submittedEnrollments: any[] = [];
		let waivedEnrollments: any[] = [];
		const _activeEnrollmentsPlanName: any[] = [];
		const _overallPremiumsPayload: any[] = [];

		submittedEnrollments = enrollments.filter((eachEnrollment: any) => {
			return eachEnrollment.enrollment_status === "SUBMITTED";
			// return eachEnrollment;
		});
		// if (enrollmentStatus?.["Helping Our Own"].enrollment === "CLOSED") {
		// 	_activeEnrollmentsPlanName.push(enrollmentStatus["Helping Our Own"]);
		// 	_overallPremiumsPayload.push({
		// 		base_plan_name: "Helping Our Own",
		// 		total_premium: 5,
		// 		enrollment_status: "SUBMITTED"
		// 	});
		// }

		submittedEnrollments.forEach((submittedEnrollment: any) => {
			// _activeEnrollmentsPlanName.push(submittedEnrollment.base_plan_name);
			_overallPremiumsPayload.push(submittedEnrollment);
		});

		waivedEnrollments = enrollments.filter((eachEnrollment: any) => {
			return eachEnrollment.enrollment_status === "WAIVED";
		});
		const pushedWaivedPlanNames: string[] = [];
		waivedEnrollments.forEach((waivedEnrollment: any) => {
			const { base_plan_name } = waivedEnrollment;
			if (
				_activeEnrollmentsPlanName.includes(base_plan_name) === false &&
				pushedWaivedPlanNames.includes(base_plan_name) === false
			) {
				_overallPremiumsPayload.push(waivedEnrollment);
				pushedWaivedPlanNames.push(base_plan_name);
			}
		});

		let _totalPremium = 0;
		_overallPremiumsPayload.map((enrollment: EnrollmentSchema) => {
			const { base_plan_name, enrollment_status, total_premium } = enrollment;
			if (base_plan_name !== currentEnrollment?.plan_name) {
				_overallPremiums.push({
					plan_name: base_plan_name,
					premium_amount: total_premium,
					status: enrollment_status
				});
				_totalPremium += total_premium;
			}
		});
		setTotalPremium(_totalPremium);
		setOverallPremiums(Object.assign([], _overallPremiums));
	}, [currentEnrollment, enrollments]);

	const handleEmailBrochurePost = useCallback(
		async (download = false) => {
			//if (!openEnrollmentByPlan) return;
			const step = urlSearchParams.get("step");
			if (!step || !member) {
				return;
			}
			// // console.log("step", step);
			let _planName = step;
			switch (step) {
				case "Health Plan":
					// _fileName = "Cancer_Kemper_Brochure_file.pdf";
					_planName = "Health Plan";
					break;
				case "Group Short Term Disability":
					// _fileName = "Whol_ Life_Kemper_Brochure_ Guide_file.pdf";
					_planName = "Group Short Term Disability";
					break;
				case "Group Long Term Disability(Class 1)":
					// _fileName = "Critica_ Illness_Kemper_Brochure_file.pdf";
					_planName = "Group Long Term Disability(Class 1)";
					break;
				case "Group Long Term Disability(Class 2)":
					// _fileName = "Short_Term_Disability_Kemper_Brochure_file.pdf";
					_planName = "Group Long Term Disability(Class 2)";
					break;
				case "KSOP":
					// _fileName = "Accident_Kemper_Brochure_file.pdf";
					_planName = "KSOP";
					break;
				case "Helping Our Own":
					// _fileName = "HI_Plans1_2_3_4 with rates 092222 (2).pdf";
					_planName = "Helping Our Own";
					break;
				case "Group Life Insurance":
					// _fileName = "Custom_EE_GLI-Brookshire_Bros_file.pdf";
					_planName = "Group Life Insurance";
					break;
			}

			const { group_number, email, first_name, last_name, employee_SSN } = member;

			let planCodeForMailingBrochure: any = alwaysOpenEnrollments
				.filter((plan: any) => plan.plan_name == _planName)
				.map((plan: any) => plan.plan_code);

			if (planCodeForMailingBrochure.length == 0) {
				planCodeForMailingBrochure = openEnrollments
					.filter((enrollment: any) => enrollment.open_enrollment.plan.plan_name == _planName)
					.map((enrollment: any) => enrollment.open_enrollment.plan.plan_code);
			}

			const payload: any = {
				email: email,
				group_number: group_number,
				// filename: openEnrollmentByPlan[step as keyof OpenEnrollmentByPlan]?.plan.document?.filename,
				type: ALL_PLANS.type.core,
				employee_class: paycheck?.employee_class,
				employee_SSN: employee_SSN,
				// plan_code: _planName,
				plan_name: _planName,
				subject: `Dear Employee: ${first_name} ${last_name} , Your plan brochure  `
			};

			if (planCodeForMailingBrochure.length > 0) {
				payload.plan_code = planCodeForMailingBrochure[0];
			}

			if (download == true) {
				payload.download = true;
			}
			const response = await trackPromise(api.email.emailBrochureV2(payload));

			if (response?.status === 200) {
				if (download != true) {
					alert("Email brochure has been successfully sent");
				} else {
					downloadBrochureFile(response.data.file.content, response.data.file.filename);
					// console.log(response);
				}
			}
		},
		[urlSearchParams, member, alwaysOpenEnrollments, paycheck?.employee_class, openEnrollments]
	);

	const handleDownloadBrochure = useCallback(() => {
		// const step = urlSearchParams.get("step");
		// switch (step) {
		// 	case "Health Plan":
		// 		downloadFile(HealthPlanBrochure, "Health_Plan_Brochure");
		// 		break;
		// 	case "Clinic Card":
		// 		downloadFile(ClinicCardPlanBrochure, "Clinic_Card_Plan_Brochure");
		// 		break;
		// 	case "Group Short Term Disability":
		// 		downloadFile(GSTDPlanBrochure, "Short_Term_Plan_Brochure");
		// 		break;
		// 	case "Group Long Term Disability(Class 1)":
		// 		downloadFile(GLTDClass1PlanBrochure, "GLTD_Class_1_Plan_Brochure");
		// 		break;
		// 	case "Group Long Term Disability(Class 2)":
		// 		downloadFile(GLTDClass2PlanBrochure, "GLTD_Class_2_Plan_Brochure");
		// 		break;
		// 	case "KSOP":
		// 		downloadFile(KSOPPlanBrochure, "KSOP_Plan_Brochure");
		// 		break;
		// 	case "Helping Our Own":
		// 		downloadFile(HelpingOurOwnPlanBrochure, "Helping_Our_Own_Plan_Brochure");
		// 		break;
		// 	case "Group Life Insurance":
		// 		downloadFile(GLIPlanBrochure, "GLI_Plan_Brochure");
		// 		break;
		// 	default:
		// 		downloadFile(Brochure, "Plan_Brochure");
		// }
		handleEmailBrochurePost(true);
	}, [handleEmailBrochurePost]);

	const handleGetCurrentEnrollmentBenefitDetailsMail = useCallback(async () => {
		const { group_number, SSN } = member;

		const currentEnrollmentQuery = {
			group_number,
			SSN
		};

		try {
			const response = await api.enrollment.getEnrollmentCurrentBenefitEmail(currentEnrollmentQuery);
			if (response) alert("Successfully the mail has been sent!");
		} catch (error) {
			alert("Eror:404Something went wrong!");
		}
	}, [member]);

	// useEffect(() => {
	// 	const step = urlSearchParams.get("step");
	// 	const stage = urlSearchParams.get("stage");
	// 	// console.log("Current step and stage:", { step, stage });

	// 	if (
	// 		step === activeTab &&
	// 		enrollmentStatus?.[activeTab as keyof EnrollmentStatus]?.enrollment === "WAIVED" &&
	// 		stage !== "2"
	// 	) {
	// 		// console.log(`Updating URL to stage 2 for ${activeTab}`);
	// 		history.replace(`?step=${activeTab}&stage=2`);
	// 	}
	// }, [enrollmentStatus, history, urlSearchParams, activeTab]);

	const renderPlan = useCallback(() => {
		if (enrollmentStatus === null || openEnrollmentByPlan === null || activeEnrollmentStatus === undefined) {
			return;
		}

		const step = urlSearchParams.get("step");
		const stage = urlSearchParams.get("stage");
		switch (step) {
			case "Health Plan": {
				if (stage === "0") {
					if (
						["ACTIVE", "REJECTED", "SUBMITTED", "WAIVED", "WAIVED - WAITING FOR APPROVAL"].includes(
							enrollmentStatus["Health Plan"].enrollment
						) &&
						openEnrollments &&
						enrollments
					) {
						return (
							<PlanAcknowledgement
								openEnrollments={openEnrollments}
								enrollments={enrollments}
								planName={"Health Plan"}
							/>
						);
					} else {
						return (
							<HealthPlanBranding
								status={enrollmentStatus["Health Plan"]}
								dependents={dependents}
								openEnrollment={openEnrollmentByPlan["Health Plan"]}
								getEnrollmentInitData={getEnrollmentInitData}
								changePlanProps={{
									handleChangePlanButtonClick: handleChangePlanButtonClick,
									buttonCaption: buttonCaption["Health Plan"]
								}}
								isActiveEnrollment={
									activeEnrollmentStatus["Health Plan" as keyof ActiveEnrollmentStatus]
								}
							/>
						);
					}
				} else if (stage === "1") {
					if (!["ACTIVE", "REJECTED"].includes(enrollmentStatus["Health Plan"].enrollment)) {
						return (
							<HealthPlanForm
								dependents={dependents}
								openEnrollment={openEnrollmentByPlan["Health Plan"]}
								getEnrollmentInitData={getEnrollmentInitData}
								isActiveEnrollment={
									activeEnrollmentStatus["Health Plan" as keyof ActiveEnrollmentStatus]
								}
							/>
						);
					}
				} else if (stage === "2") {
					return (
						<PlanAcknowledgement
							openEnrollments={openEnrollments}
							enrollments={enrollments}
							planName={"Health Plan"}
						/>
					);
				}
				break;
			}
			case "Group Short Term Disability": {
				if (stage === "0") {
					if (
						["ACTIVE", "REJECTED", "SUBMITTED", "WAIVED", "WAIVED - WAITING FOR APPROVAL"].includes(
							enrollmentStatus["Group Short Term Disability"].enrollment
						) &&
						openEnrollments &&
						enrollments
					) {
						return (
							<PlanAcknowledgement
								openEnrollments={openEnrollments}
								enrollments={enrollments}
								planName={"Group Short Term Disability"}
							/>
						);
					} else {
						return (
							<GstdBranding
								status={enrollmentStatus["Group Short Term Disability"]}
								dependents={dependents}
								openEnrollment={openEnrollmentByPlan["Group Short Term Disability"]}
								changePlanProps={{
									handleChangePlanButtonClick: handleChangePlanButtonClick,
									buttonCaption: buttonCaption["Group Short Term Disability"]
								}}
								getEnrollmentInitData={getEnrollmentInitData}
								isActiveEnrollment={
									activeEnrollmentStatus[
										"Group Short Term Disability" as keyof ActiveEnrollmentStatus
									]
								}
							/>
						);
					}
				} else if (stage === "1") {
					if (!["ACTIVE", "REJECTED"].includes(enrollmentStatus["Group Short Term Disability"].enrollment)) {
						return (
							<GstdForm
								dependents={dependents}
								openEnrollment={openEnrollmentByPlan["Group Short Term Disability"]}
								getEnrollmentInitData={getEnrollmentInitData}
								isActiveEnrollment={
									activeEnrollmentStatus[
										"Group Short Term Disability" as keyof ActiveEnrollmentStatus
									]
								}
							/>
						);
					}
				} else if (stage === "2") {
					return (
						<PlanAcknowledgement
							openEnrollments={openEnrollments}
							enrollments={enrollments}
							planName={"Group Short Term Disability"}
						/>
					);
				}
				break;
			}
			case "Group Long Term Disability(Class 1)": {
				if (stage === "0") {
					if (
						["ACTIVE", "REJECTED", "SUBMITTED", "WAIVED", "WAIVED - WAITING FOR APPROVAL"].includes(
							enrollmentStatus["Group Long Term Disability(Class 1)"].enrollment
						) &&
						openEnrollments &&
						enrollments
					) {
						return (
							<PlanAcknowledgement
								openEnrollments={openEnrollments}
								enrollments={enrollments}
								planName={"Group Long Term Disability(Class 1)"}
							/>
						);
					} else {
						return (
							<GltdBranding
								status={enrollmentStatus["Group Long Term Disability(Class 1)"]}
								dependents={dependents}
								changePlanProps={{
									handleChangePlanButtonClick: handleChangePlanButtonClick,
									buttonCaption: buttonCaption["Group Long Term Disability(Class 1)"]
								}}
								openEnrollment={openEnrollmentByPlan["Group Long Term Disability(Class 1)"]}
								getEnrollmentInitData={getEnrollmentInitData}
							/>
						);
					}
				} else if (stage === "1") {
					if (
						!["ACTIVE", "REJECTED"].includes(
							enrollmentStatus["Group Long Term Disability(Class 1)"].enrollment
						)
					) {
						return (
							<GltdForm
								dependents={dependents}
								openEnrollment={openEnrollmentByPlan["Group Long Term Disability(Class 1)"]}
								getEnrollmentInitData={getEnrollmentInitData}
							/>
						);
					}
				} else if (stage === "2") {
					return (
						<PlanAcknowledgement
							openEnrollments={openEnrollments}
							enrollments={enrollments}
							planName={"Group Long Term Disability(Class 1)"}
						/>
					);
				}
				break;
			}
			case "Group Long Term Disability(Class 2)": {
				if (stage === "0") {
					if (
						["ACTIVE", "REJECTED", "SUBMITTED", "WAIVED", "WAIVED - WAITING FOR APPROVAL"].includes(
							enrollmentStatus["Group Long Term Disability(Class 2)"].enrollment
						) &&
						openEnrollments &&
						enrollments
					) {
						return (
							<PlanAcknowledgement
								openEnrollments={openEnrollments}
								enrollments={enrollments}
								planName={"Group Long Term Disability(Class 2)"}
							/>
						);
					} else {
						return (
							<GLTDClass2Branding
								status={enrollmentStatus["Group Long Term Disability(Class 2)"]}
								dependents={dependents}
								changePlanProps={{
									handleChangePlanButtonClick: handleChangePlanButtonClick,
									buttonCaption: buttonCaption["Group Long Term Disability(Class 2)"]
								}}
								openEnrollment={openEnrollmentByPlan["Group Long Term Disability(Class 2)"]}
								getEnrollmentInitData={getEnrollmentInitData}
								isActiveEnrollment={
									activeEnrollmentStatus[
										"Group Long Term Disability(Class 2)" as keyof ActiveEnrollmentStatus
									]
								}
							/>
						);
					}
				} else if (stage === "1") {
					if (
						!["ACTIVE", "REJECTED"].includes(
							enrollmentStatus["Group Long Term Disability(Class 2)"].enrollment
						)
					) {
						return (
							<LincolnForm
								dependents={dependents}
								openEnrollment={openEnrollmentByPlan["Group Long Term Disability(Class 2)"]}
								getEnrollmentInitData={getEnrollmentInitData}
								isActiveEnrollment={
									activeEnrollmentStatus[
										"Group Long Term Disability(Class 2)" as keyof ActiveEnrollmentStatus
									]
								}
							/>
						);
					}
				} else if (stage === "2") {
					return (
						<PlanAcknowledgement
							openEnrollments={openEnrollments}
							enrollments={enrollments}
							planName={"Group Long Term Disability(Class 2)"}
						/>
					);
				}
				break;
			}
			case "KSOP": {
				if (stage === "0") {
					if (
						["ACTIVE", "REJECTED", "SUBMITTED", "WAIVED", "WAIVED - WAITING FOR APPROVAL"].includes(
							enrollmentStatus["KSOP"].enrollment
						) &&
						openEnrollments &&
						enrollments
					) {
						return (
							<PlanAcknowledgement
								openEnrollments={openEnrollments}
								enrollments={enrollments}
								planName={"KSOP"}
							/>
						);
					} else {
						return (
							<KsopBranding
								status={enrollmentStatus["KSOP"]}
								dependents={dependents}
								changePlanProps={{
									handleChangePlanButtonClick: handleChangePlanButtonClick,
									buttonCaption: buttonCaption["KSOP"]
								}}
								openEnrollment={openEnrollmentByPlan["KSOP"]}
								getEnrollmentInitData={getEnrollmentInitData}
							/>
						);
					}
				} else if (stage === "1") {
					if (!["ACTIVE", "REJECTED"].includes(enrollmentStatus["KSOP"].enrollment)) {
						return (
							<KsopForm
								dependents={dependents}
								openEnrollment={openEnrollmentByPlan["KSOP"]}
								getEnrollmentInitData={getEnrollmentInitData}
							/>
						);
					}
				} else if (stage === "2") {
					return (
						<PlanAcknowledgement
							openEnrollments={openEnrollments}
							enrollments={enrollments}
							planName={"KSOP"}
						/>
					);
				}
				break;
			}
			case "Clinic Card": {
				// console.log("Clinic Card case triggered");
				// console.log("Current stage:", stage);
				// console.log("Enrollment status:", enrollmentStatus["Clinic Card"].enrollment);

				if (stage === "0") {
					// console.log("Stage 0 for Clinic Card");
					if (
						["ACTIVE", "REJECTED", "SUBMITTED", "WAIVED", "WAIVED - WAITING FOR APPROVAL"].includes(
							enrollmentStatus["Clinic Card"].enrollment
						) &&
						openEnrollments &&
						enrollments
					) {
						// console.log("Rendering PlanAcknowledgement for Clinic Card");
						return (
							<PlanAcknowledgement
								openEnrollments={openEnrollments}
								enrollments={enrollments}
								planName={"Clinic Card"}
							/>
						);
					} else {
						// console.log("Rendering ClinicCardBranding for Clinic Card");
						return (
							<ClinicCardBranding
								status={enrollmentStatus["Clinic Card"]}
								healthPlanStatus={enrollmentStatus["Health Plan"]}
								changePlanProps={{
									handleChangePlanButtonClick: handleChangePlanButtonClick,
									buttonCaption: buttonCaption["Clinic Card"]
								}}
								dependents={dependents}
								openEnrollment={openEnrollmentByPlan["Clinic Card"]}
								getEnrollmentInitData={getEnrollmentInitData}
								isActiveEnrollment={
									activeEnrollmentStatus["Clinic Card" as keyof ActiveEnrollmentStatus]
								}
							/>
						);
					}
				} else if (stage === "1") {
					// console.log("Stage 1 for Clinic Card");
					if (!["ACTIVE", "REJECTED"].includes(enrollmentStatus["Clinic Card"].enrollment)) {
						// console.log("Rendering ClinicCardForm for Clinic Card");
						return (
							<ClinicCardForm
								dependents={dependents}
								openEnrollment={openEnrollmentByPlan["Clinic Card"]}
								getEnrollmentInitData={getEnrollmentInitData}
								isActiveEnrollment={
									activeEnrollmentStatus["Clinic Card" as keyof ActiveEnrollmentStatus]
								}
							/>
						);
					}
				} else if (stage === "2") {
					// console.log("Stage 2 for Clinic Card");
					// console.log("Rendering PlanAcknowledgement for Clinic Card");
					return (
						<PlanAcknowledgement
							openEnrollments={openEnrollments}
							enrollments={enrollments}
							planName={"Clinic Card"}
						/>
					);
				}
				break;
			}
			case "Helping Our Own": {
				if (stage === "0") {
					if (
						["ACTIVE", "REJECTED", "SUBMITTED", "WAIVED"].includes(
							enrollmentStatus["Helping Our Own"].enrollment
						) &&
						alwaysOpenEnrollments &&
						enrollments
					) {
						return (
							<AlwaysOpenPlanAcknowledgement
								enrollments={enrollments}
								planName={"Helping Our Own"}
								alwaysOpenEnrollments={alwaysOpenEnrollments}
							/>
						);
					} else {
						return (
							<HOOBranding
								status={enrollmentStatus["Helping Our Own"]}
								dependents={dependents}
								openEnrollment={openEnrollmentByPlan["Helping Our Own"]}
								changePlanProps={{
									handleChangePlanButtonClick: handleChangePlanButtonClick,
									buttonCaption: buttonCaption["Helping Our Own"]
								}}
								getEnrollmentInitData={getEnrollmentInitData}
								alwaysOpenEnrollments={alwaysOpenEnrollments}
								planName={"Helping Our Own"}
							/>
						);
					}
				} else if (stage === "1") {
					if (!["REJECTED"].includes(enrollmentStatus["Helping Our Own"].enrollment)) {
						return (
							<PDForm
								dependents={dependents}
								openEnrollment={openEnrollmentByPlan["Helping Our Own"]}
								getEnrollmentInitData={getEnrollmentInitData}
								alwaysOpenEnrollments={alwaysOpenEnrollments}
								planName={"Helping Our Own"}
							/>
						);
					}
				} else if (stage === "2") {
					return (
						<AlwaysOpenPlanAcknowledgement
							enrollments={enrollments}
							planName={"Helping Our Own"}
							alwaysOpenEnrollments={alwaysOpenEnrollments}
						/>
					);
				}
				// return <PDForm />;
				break;
			}
			case "Christmas Club Election": {
				if (stage === "0") {
					if (
						["ACTIVE", "REJECTED", "SUBMITTED", "WAIVED", "WAIVED - WAITING FOR APPROVAL"].includes(
							enrollmentStatus["Christmas Club Election"].enrollment
						) &&
						alwaysOpenEnrollments &&
						enrollments
					) {
						return (
							<AlwaysOpenPlanAcknowledgement
								enrollments={enrollments}
								planName={"Christmas Club Election"}
								alwaysOpenEnrollments={alwaysOpenEnrollments}
							/>
						);
					} else {
						return (
							<CCeBranding
								status={enrollmentStatus["Christmas Club Election"]}
								dependents={dependents}
								openEnrollment={openEnrollmentByPlan["Christmas Club Election"]}
								changePlanProps={{
									handleChangePlanButtonClick: handleChangePlanButtonClick,
									buttonCaption: buttonCaption["Christmas Club Election"]
								}}
								getEnrollmentInitData={getEnrollmentInitData}
								alwaysOpenEnrollments={alwaysOpenEnrollments}
								planName={"Christmas Club Election"}
							/>
						);
					}
				} else if (stage === "1") {
					if (!["REJECTED"].includes(enrollmentStatus["Christmas Club Election"].enrollment)) {
						return (
							<CceForm
								dependents={dependents}
								openEnrollment={openEnrollmentByPlan["Christmas Club Election"]}
								getEnrollmentInitData={getEnrollmentInitData}
								alwaysOpenEnrollments={alwaysOpenEnrollments}
								planName={"Christmas Club Election"}
								enrollments={enrollments}
							/>
						);
					}
				} else if (stage === "2") {
					return (
						<AlwaysOpenPlanAcknowledgement
							enrollments={enrollments}
							planName={"Christmas Club Election"}
							alwaysOpenEnrollments={alwaysOpenEnrollments}
						/>
					);
				}
				break;
			}
			case "Group Life Insurance": {
				if (stage === "0") {
					if (
						["ACTIVE", "REJECTED", "SUBMITTED", "WAIVED", "WAIVED - WAITING FOR APPROVAL"].includes(
							enrollmentStatus["Group Life Insurance"].enrollment
						) &&
						openEnrollments &&
						enrollments
					) {
						return (
							<PlanAcknowledgement
								openEnrollments={openEnrollments}
								enrollments={enrollments}
								planName={"Group Life Insurance"}
							/>
						);
					} else {
						return (
							<GliBranding
								status={enrollmentStatus["Group Life Insurance"]}
								dependents={dependents}
								changePlanProps={{
									handleChangePlanButtonClick: handleChangePlanButtonClick,
									buttonCaption: buttonCaption["Group Long Term Disability(Class 2)"]
								}}
								openEnrollment={openEnrollmentByPlan["Group Life Insurance"]}
								getEnrollmentInitData={getEnrollmentInitData}
								isActiveEnrollment={
									activeEnrollmentStatus["Group Life Insurance" as keyof ActiveEnrollmentStatus]
								}
							/>
						);
					}
				} else if (stage === "1") {
					if (!["ACTIVE", "REJECTED"].includes(enrollmentStatus["Group Life Insurance"].enrollment)) {
						return (
							// <GLIForm
							// 	dependents={dependents}
							// 	openEnrollment={openEnrollmentByPlan["Group Life Insurance"]}
							// 	getEnrollmentInitData={getEnrollmentInitData}
							// />

							//New form for GLI
							<GLIFormv2
								dependents={dependents}
								openEnrollment={openEnrollmentByPlan["Group Life Insurance"]}
								getEnrollmentInitData={getEnrollmentInitData}
								isActiveEnrollment={
									activeEnrollmentStatus["Group Life Insurance" as keyof ActiveEnrollmentStatus]
								}
							/>
						);
					}
				} else if (stage === "2") {
					return (
						<PlanAcknowledgement
							openEnrollments={openEnrollments}
							enrollments={enrollments}
							planName={"Group Life Insurance"}
						/>
					);
				}
				break;
			}
		}
	}, [
		activeEnrollmentStatus,
		dependents,
		enrollmentStatus,
		enrollments,
		getEnrollmentInitData,
		openEnrollmentByPlan,
		openEnrollments,
		urlSearchParams,
		currentStage
	]);

	// useEffect(() => {
	// 	handleButtonCaptionSetter();
	// }, [handleButtonCaptionSetter]);

	useEffect(() => {
		getEnrollmentInitData(true);
		initialVisitedPlans();
		initNextOfCurrentPlans();
	}, [getEnrollmentInitData, initialVisitedPlans, initNextOfCurrentPlans]);

	useEffect(() => {
		getOverallPremium();
	}, [getOverallPremium, enrollments]);

	useEffect(() => {
		if (hasSetPlanTabs === true) {
			setActiveTab(planTabs[0].plan_name);
		}
	}, [hasSetPlanTabs, planTabs]);

	useEffect(() => {
		if (currentEnrollment) {
			const _overallPremiums: any[] = [];
			let _totalPremium = 0;
			enrollments.forEach((VEBEnrollment: EnrollmentSchema) => {
				const { base_plan_name, premium_amount, enrollment_status } = VEBEnrollment;
				if (base_plan_name !== currentEnrollment?.plan_name) {
					_overallPremiums.push({
						plan_name: base_plan_name,
						premium_amount: premium_amount,
						status: enrollment_status
					});
					_totalPremium += premium_amount;
				}
			});
			setTotalPremium(() => Number((_totalPremium + currentEnrollment.premium_amount).toFixed(2)));
		}
	}, [currentEnrollment, enrollments]);

	useEffect(() => {
		if (activeTab) {
			history.push("?step=" + activeTab + "&stage=0");
		}
	}, [activeTab, history]);

	useEffect(() => {
		if (
			!urlSearchParams ||
			urlSearchParams == undefined ||
			urlSearchParams.get("step") == undefined ||
			!urlSearchParams.get("step")
		) {
			if (activeTab) {
				history.push("?step=" + activeTab + "&stage=0");
			} else {
				if (hasSetPlanTabs === true) {
					setActiveTab(planTabs[0].plan_name);
				}
			}
		}
	}, [activeTab, hasSetPlanTabs, history, planTabs, urlSearchParams, visitedPlans]);

	// // console.log(urlSearchParams, "___need_state : activeTab", activeTab, "\nVisited", visitedPlans);

	useEffect(() => {
		// // console.log("i am running", urlSearchParams);
		if (
			!urlSearchParams ||
			urlSearchParams == undefined ||
			urlSearchParams.get("step") == undefined ||
			!urlSearchParams.get("step")
		) {
			if (activeTab) {
				history.push("?step=" + activeTab + "&stage=0");
			} else {
				if (hasSetPlanTabs === true) {
					setActiveTab(planTabs[0].plan_name);
				}
			}
		}
	}, [activeTab, hasSetPlanTabs, history, planTabs, urlSearchParams, visitedPlans]);

	// // console.log(urlSearchParams, "___need_state : activeTab", activeTab, "\nVisited", visitedPlans);

	useEffect(() => {
		if (urlSearchParams.get("stage") === "2") {
			// const stage = urlSearchParams.get("stage");
			// // console.log("Fetching enrollments for stage 2...");
			getEnrollmentInitData(false);
		}
	}, [urlSearchParams.get("stage")]);

	console.log("===openEnrollments===>", openEnrollments);

	//return <></>;
	return (
		<>
			<div className="enrollment">
				<Box
					className="box-container"
					sx={{
						flexGrow: 1,
						//bgcolor: "background.paper",
						display: "block",
						marginLeft: "-2rem"
					}}
				>
					<Tabs
						orientation="vertical"
						className="tabs-cotainer"
						// variant="scrollable"
						//variant="fullWidth"
						style={{
							// boxShadow:
							//"rgba(50, 50, 93, 0.25) 0px 30px 60px -12px inset, rgba(0, 0, 0, 0.3) 0px 18px 36px -18px inset",
							boxShadow: "rgba(0, 0, 0, 0.3) 0px 19px 38px, rgba(0, 0, 0, 0.22) 0px 15px 12px",
							borderTopLeftRadius: 20,
							borderTopRightRadius: 20,
							borderBottomLeftRadius: 20,
							borderBottomRightRadius: 20,
							color: theme.primary_color
						}}
						TabIndicatorProps={{
							style: {
								color: theme.primary_color,
								backgroundColor: theme.primary_color
							}
						}}
						value={value}
						onChange={handleChange}
					>
						{enrollmentStatus &&
							activeEnrollmentStatus &&
							planTabs.map((planTab: PlanTab, index: number) => {
								const { plan_name } = planTab;
								return (
									<Tab
										key={index}
										className={
											activeTab == plan_name
												? visitedPlans.includes(plan_name)
													? "active-tab tracker"
													: "active-tab"
												: visitedPlans.includes(plan_name)
												? "active-tab tracker"
												: "active-tab"
										}
										style={{
											height: (height - 500) / planTabs.length,
											color: activeTab == plan_name ? theme.primary_color : undefined
										}}
										value={plan_name}
										label={
											enrollmentStatus[plan_name as keyof EnrollmentStatus]?.enrollment ? (
												<EnrollmentTabLabel
													planName={plan_name}
													status={
														["Helping Our Own", "Christmas Club Election"].includes(
															plan_name
														)
															? "OPENED"
															: enrollmentStatus[plan_name as keyof EnrollmentStatus]
																	.enrollment
													}
													isOpenEnrollment={
														["Helping Our Own", "Christmas Club Election"].includes(
															plan_name
														)
															? true
															: enrollmentStatus[plan_name as keyof EnrollmentStatus]
																	.openEnrollment === "OPENED"
															? true
															: false
													}
													isActiveEnrollment={
														activeEnrollmentStatus[
															plan_name as keyof ActiveEnrollmentStatus
														]
													}
													carrier="Kemper"
												/>
											) : null
										}
										{...a11yProps(0)}
									/>
								);
							})}
					</Tabs>
					<div className="form-container">{renderPlan()}</div>
				</Box>
				{showOverallPremium ? (
					<Draggable>
						<div className="draggable-premium-box">
							<div>
								<div
									className={"top-bar-expand"}
									style={{ backgroundColor: theme.button.background_color }}
								>
									<div className="header" style={{ color: theme.button.color }}>
										Overall Premium (Current OE)
									</div>
									<div className={"icon-expand"}>
										<IconButton onClick={handleOverallPremiumClick}>
											<KeyboardArrowRightIcon style={{ color: theme.button.color }} />
										</IconButton>
									</div>
								</div>
							</div>
							<div>
								<div
									style={{
										backgroundColor: "#e3e1e1",
										padding: "1rem",
										display: "flex",
										fontWeight: "bold",
										alignItems: "center",
										gap: "20%"
									}}
								>
									<div className="header" style={{ color: "#242424", flex: "1" }}>
										{/* <span>VEB</span> */}
										<span>Group&nbsp;Plan(s)</span>
									</div>

									<div className="header" style={{ fontSize: "1rem", marginLeft: "2rem", flex: "3" }}>
										Premium&nbsp;($)
									</div>
									<div
										className="header"
										style={{ color: "#242424", marginRight: "12rem", flex: "1" }}
									>
										Status
									</div>
								</div>
							</div>
							<TableContainer
								component={Paper}
								style={{ zIndex: 99999, maxHeight: "300px", overflowY: "auto" }}
							>
								<Table sx={{ minWidth: 300 }} aria-label="customized table">
									{/* <TableHead>
										<TableRow>
											<StyledTableCell>
												<span className="premium-table-header">Plan</span>
											</StyledTableCell>
											<StyledTableCell align="right">
												<span className="premium-table-header">Premium&nbsp;($)</span>
											</StyledTableCell>
											<StyledTableCell align="right">
												<span className="premium-table-header">Status</span>
											</StyledTableCell>
										</TableRow>
									</TableHead> */}
									<TableBody>
										{overallPremiums.map((overallPremium: OverallPremiums, index: number) => (
											<StyledTableRow key={index}>
												<StyledTableCell component="th" scope="row">
													{overallPremium.plan_name}
												</StyledTableCell>
												<StyledTableCell align="right">
													{overallPremium.premium_amount.toFixed(2)}
												</StyledTableCell>
												{overallPremium.status ? (
													<StyledTableCell align="right">
														<Chip
															label={initCapitalize(overallPremium.status)}
															style={{
																backgroundColor: `${
																	overallPremium.status === "SUBMITTED"
																		? overallPremiumChipColor.SUBMIITED
																				.background_color
																		: overallPremium.status === "WAIVED"
																		? overallPremiumChipColor.WAIVED
																				.background_color
																		: null
																}`,
																color: `${
																	overallPremium.status === "SUBMITTED"
																		? overallPremiumChipColor.SUBMIITED.text_color
																		: overallPremium.status === "WAIVED"
																		? overallPremiumChipColor.WAIVED.text_color
																		: null
																}`,
																fontWeight: "700"
															}}
														/>
													</StyledTableCell>
												) : null}
											</StyledTableRow>
										))}
										{currentEnrollment ? (
											<StyledTableRow>
												<StyledTableCell component="th" scope="row">
													{currentEnrollment.plan_name}
												</StyledTableCell>
												<StyledTableCell align="right">
													{currentEnrollment.premium_amount}
												</StyledTableCell>
												<StyledTableCell align="right">
													<Chip
														label={initCapitalize(currentEnrollment.status)}
														style={{
															backgroundColor:
																overallPremiumChipColor.CURRENT.background_color,
															color: overallPremiumChipColor.CURRENT.text_color,
															fontWeight: "700"
														}}
													/>
												</StyledTableCell>
											</StyledTableRow>
										) : null}
									</TableBody>
								</Table>
							</TableContainer>
							<div>
								<div
									style={{
										backgroundColor: "#e3e1e1",
										padding: "0.3rem",
										display: "flex",
										fontWeight: "bold",
										alignItems: "center",
										gap: "20%"
									}}
								>
									<div className="header" style={{ color: "#242424", marginLeft: "1rem" }}>
										TOTAL AMOUNT
									</div>

									<div className="show-premium" style={{ fontSize: "1.3rem" }}>
										{dollarize(Number(totalPremium.toFixed(2))) + (currentEnrollment ? "*" : "")}
									</div>
								</div>
							</div>
						</div>
					</Draggable>
				) : (
					<Draggable axis="y">
						<div className="draggable-premium-box">
							<div>
								<div className={"top-bar"} style={{ backgroundColor: theme.button.background_color }}>
									<div className="header" style={{ color: theme.button.color }}>
										Overall Amount
									</div>
									<div className={"icon"}>
										<IconButton onClick={handleOverallPremiumClick}>
											<KeyboardArrowRightIcon style={{ color: theme.button.color }} />
										</IconButton>
									</div>
								</div>
							</div>
						</div>
					</Draggable>
				)}
			</div>
			{urlSearchParams.get("step") !== "Christmas Club Election" ? (
				<SpeedDial
					ariaLabel="SpeedDial tooltip example"
					sx={{ position: "fixed", bottom: 16, right: 16 }}
					icon={<SpeedDialIcon />}
					onOpen={handleOpen}
					onClose={handleClose}
					open={speedDialOpen}
					style={{ fontSize: "10px" }}
					FabProps={{
						sx: {
							bgcolor: theme.primary_color,
							"&:hover": {
								bgcolor: theme.primary_color
							}
						}
					}}
				>
					{speedDialActions.map((speedDialAction: any) => (
						<SpeedDialAction
							key={speedDialAction.name}
							icon={speedDialAction.icon}
							tooltipTitle={speedDialAction.name}
							tooltipOpen
							onClick={
								speedDialAction.actionName === "email_brochure"
									? handleEmailBrochurePost
									: // : () => downloadFile(Brochure)
									  handleDownloadBrochure
							}
							style={{ fontSize: "10px" }}
						/>
					))}
				</SpeedDial>
			) : null}
			<Tooltip title="Get Current Benefit Details">
				<SpeedDial
					ariaLabel="SpeedDial tooltip example"
					sx={{ position: "fixed", bottom: 16, right: 100 }}
					icon={<EmailIcon />}
					onOpen={() => null}
					onClose={() => null}
					onClick={handleGetCurrentEnrollmentBenefitDetailsMail}
					style={{ fontSize: "10px" }}
					FabProps={{
						sx: {
							bgcolor: theme.primary_color,
							"&:hover": {
								bgcolor: theme.primary_color
							}
						}
					}}
				></SpeedDial>
			</Tooltip>
		</>
	);
};

export default Enrollment;
